/**
 * Auth Cookies
 *
 * Cookie configuration and helpers for auth token storage.
 * The cookie contains an encrypted JWT payload (access + refresh tokens).
 */

import type { ResponseCookie } from 'next/dist/compiled/@edge-runtime/cookies';
import type { NextResponse } from 'next/server';

/**
 * Cookie name for the encrypted auth token
 */
export const AUTH_COOKIE_NAME = 'auth';

/**
 * Cookie name for user data (core profile)
 * Contains: id, email, firstName, lastName, metadata
 * Does NOT contain: addresses (stored separately), tokens, passwords
 */
export const USER_COOKIE_NAME = 'user';

/**
 * Cookie name for user addresses (split from user cookie for size)
 * Contains: addresses, defaultBillingAddress, defaultShippingAddress
 */
export const USER_ADDRESSES_COOKIE_NAME = 'user_addresses';

/**
 * Cookie options for the auth token
 * - httpOnly: Prevents JavaScript access (security)
 * - secure: Only sent over HTTPS in production
 * - sameSite: Lax allows navigation while preventing CSRF
 * - path: Available on all routes
 * - maxAge: 30 days (matches refresh token lifetime)
 */
export const AUTH_COOKIE_OPTIONS: Partial<ResponseCookie> = {
  httpOnly: true,
  secure: process.env.NODE_ENV === 'production',
  sameSite: 'lax',
  path: '/',
  maxAge: 60 * 60 * 24 * 30, // 30 days in seconds
};

/**
 * Cookie options for the user data
 * - NOT httpOnly: Can be read by server components
 * - secure: Only sent over HTTPS in production
 * - sameSite: Lax allows navigation while preventing CSRF
 * - path: Available on all routes
 * - maxAge: 30 days (matches auth cookie)
 */
export const USER_COOKIE_OPTIONS: Partial<ResponseCookie> = {
  httpOnly: false, // Readable by server components
  secure: process.env.NODE_ENV === 'production',
  sameSite: 'lax',
  path: '/',
  maxAge: 60 * 60 * 24 * 30, // 30 days in seconds
};

/**
 * Cookie options for clearing the auth cookie
 */
export const AUTH_COOKIE_CLEAR_OPTIONS: Partial<ResponseCookie> = {
  httpOnly: true,
  secure: process.env.NODE_ENV === 'production',
  sameSite: 'lax',
  path: '/',
  maxAge: 0, // Expire immediately
};

/**
 * Clear auth session cookies on a proxy or API response (terminal auth failure / logout).
 *
 * Example Input:
 * NextResponse from proxy after refresh failed with a stale auth cookie present
 *
 * Example Output:
 * response Set-Cookie headers clearing auth, user, and user_addresses
 */
export function clearAuthCookiesOnResponse(response: NextResponse): void {
  response.cookies.set(AUTH_COOKIE_NAME, '', AUTH_COOKIE_CLEAR_OPTIONS);
  response.cookies.set(USER_COOKIE_NAME, '', { ...AUTH_COOKIE_CLEAR_OPTIONS, httpOnly: false });
  response.cookies.set(USER_ADDRESSES_COOKIE_NAME, '', {
    ...AUTH_COOKIE_CLEAR_OPTIONS,
    httpOnly: false,
  });
}
